// Extended i18n dictionary for Phase-1 features (pricing, team, contracts, calendar, PDF, billing)
import { dict } from "@/lib/i18n";

const extra = {
  en: {
    nav_ext: { pricing: "Pricing", team: "Team", templates: "Contracts", calendar: "Calendar", lawfirm: "Contact a Law Firm" },
    pricing: {
      kicker: "Plans for every Lebanese business",
      title: "Pay for clarity. Not the billable hour.",
      sub: "Three tiers. Cancel anytime. Paid subscriptions launching soon — start free today.",
      mo: "/ month",
      cta: "Subscribe",
      current: "Current plan",
      upgrade: "Upgrade",
      best: "Most popular",
      already: "You are subscribed to",
      free_label: "Free — 5 AI consultations",
      manage: "Manage subscription",
      coming_soon: "Coming soon",
    },
    billing: {
      success_title: "Payment received",
      success_sub: "Your plan is being activated…",
      verifying: "Verifying payment with Stripe…",
      go_dashboard: "Go to dashboard",
      cancelled: "Payment cancelled. You can try again any time.",
    },
    team: {
      title: "Team workspace",
      sub: "Invite colleagues, manage seats, and collaborate on consultations.",
      members: "Members", invites: "Pending invitations", seats: "Seats used",
      invite_email: "Colleague's email", role: "Role", send: "Send invite",
      revoke: "Revoke", remove: "Remove",
      seat_limit_reached: "Seat limit reached. Upgrade your plan to invite more colleagues.",
      copy_link: "Copy invite link", copied: "Invite link copied",
      rename: "Rename workspace", workspace_name: "Workspace name",
      no_members: "No team members yet.", no_invites: "No pending invites.",
      join_title: "Join workspace",
      join_sub: "You've been invited to join",
      join_btn: "Accept invitation",
      join_need_login: "Sign in (or create an account) with the invited email to accept.",
    },
    contracts: {
      title: "Contract templates",
      sub: "Generate Lebanon-compliant contracts in seconds. Review with counsel before signing.",
      pick: "Choose a template",
      generate: "Generate contract",
      generating: "Drafting with Law Pilot…",
      preview: "Preview",
      download_pdf: "Download PDF",
      history: "Your contracts",
      no_history: "No contracts generated yet.",
      delete: "Delete",
      output_lang: "Output language",
    },
    calendar: {
      title: "Fiscal & labour calendar",
      sub: "Lebanon's 2026 statutory deadlines. Add your company-specific reminders.",
      add_event: "Add reminder",
      event_title: "Title",
      event_date: "Date",
      event_note: "Note (optional)",
      save: "Save",
      no_events: "No upcoming events.",
      categories: {
        nssf: "NSSF", vat: "VAT", payroll_tax: "Payroll Tax",
        corporate_tax: "Corporate Tax", personal_income_tax: "Income Tax",
        property_tax: "Property Tax", commercial_register: "Commercial Register",
        custom: "Custom",
      },
      delete: "Delete",
      custom_only: "Only custom reminders can be deleted.",
    },
    chat_ext: { export_pdf: "Export as PDF", quota_warn: "You've used your 5 free consultations for this period. Paid plans are launching very soon — until then, message us on WhatsApp and we'll upgrade you manually.", upgrade_link: "Upgrade plan", talk_to_lawyer: "Talk to a lawyer about this", upgrade_whatsapp: "Message us on WhatsApp to upgrade" },
    dash_ext: { quota_remaining: "consultations remaining", unlimited: "unlimited", plan: "Plan" },
    lawfirm: {
      kicker: "When Law Pilot isn't enough",
      title: "Talk to a licensed Lebanese lawyer",
      sub: "For binding advice, representation in court, or matters that require a wet signature — speak to a partner at Eid Law Firm.",
      firm_desc: "Eid Law Firm is a Lebanese practice handling corporate, commercial, labour and tax matters for businesses operating in Lebanon and abroad.",
      whatsapp_cta: "Chat on WhatsApp",
      prefill_label: "Message preview",
      when_kicker: "WHEN TO ESCALATE",
      when_title: "Situations that need a licensed lawyer",
      when_items: [
        "A dispute is filed in court, or you've received a summons or claim",
        "You need to sign or register a binding contract or notarial deed",
        "Negotiating an exit, settlement or M&A transaction",
        "Drafting a complex shareholders' or share-purchase agreement",
        "Defending an arbitrary-dismissal or end-of-service claim",
        "A tax assessment is disputed or under audit by the MoF",
        "Anything criminal, regulatory, or with personal liability exposure",
      ],
      note: "By contacting Eid Law Firm you create a direct attorney-client relationship governed by the Lebanese Bar Association rules. Law Pilot does not take a fee from this referral.",
      copy_number: "Copy phone number",
      copied: "Phone number copied to clipboard",
    },
  },
  fr: {
    nav_ext: { pricing: "Tarifs", team: "Équipe", templates: "Contrats", calendar: "Calendrier", lawfirm: "Contacter un cabinet" },
    pricing: {
      kicker: "Des plans pour chaque entreprise libanaise",
      title: "Payez pour la clarté. Pas l’heure facturée.",
      sub: "Trois formules. Résiliable à tout moment. Abonnements payants bientôt disponibles — commencez gratuitement.",
      mo: "/ mois", cta: "S’abonner", current: "Plan actuel", upgrade: "Mettre à niveau",
      best: "Le plus populaire", already: "Vous êtes abonné à",
      free_label: "Gratuit — 5 consultations",
      manage: "Gérer l’abonnement",
      coming_soon: "Bientôt disponible",
    },
    billing: {
      success_title: "Paiement reçu",
      success_sub: "Activation de votre plan…",
      verifying: "Vérification du paiement avec Stripe…",
      go_dashboard: "Aller au tableau de bord",
      cancelled: "Paiement annulé. Vous pouvez réessayer à tout moment.",
    },
    team: {
      title: "Espace d’équipe",
      sub: "Invitez vos collègues, gérez les sièges, collaborez sur les consultations.",
      members: "Membres", invites: "Invitations en attente", seats: "Sièges utilisés",
      invite_email: "Email du collègue", role: "Rôle", send: "Envoyer l’invitation",
      revoke: "Révoquer", remove: "Retirer",
      seat_limit_reached: "Limite de sièges atteinte. Passez à un plan supérieur.",
      copy_link: "Copier le lien", copied: "Lien copié",
      rename: "Renommer l’espace", workspace_name: "Nom de l’espace",
      no_members: "Aucun membre.", no_invites: "Aucune invitation en attente.",
      join_title: "Rejoindre l’espace",
      join_sub: "Vous êtes invité à rejoindre",
      join_btn: "Accepter l’invitation",
      join_need_login: "Connectez-vous (ou créez un compte) avec l’email invité pour accepter.",
    },
    contracts: {
      title: "Modèles de contrats",
      sub: "Générez des contrats conformes au droit libanais. À faire valider par votre conseil avant signature.",
      pick: "Choisir un modèle", generate: "Générer le contrat",
      generating: "Rédaction par Law Pilot…", preview: "Aperçu",
      download_pdf: "Télécharger en PDF", history: "Vos contrats",
      no_history: "Aucun contrat généré.", delete: "Supprimer",
      output_lang: "Langue de sortie",
    },
    calendar: {
      title: "Calendrier fiscal & du travail",
      sub: "Échéances libanaises 2026. Ajoutez vos rappels spécifiques.",
      add_event: "Ajouter un rappel", event_title: "Titre", event_date: "Date",
      event_note: "Note (optionnel)", save: "Enregistrer",
      no_events: "Aucun événement.",
      categories: {
        nssf: "CNSS", vat: "TVA", payroll_tax: "Impôt salaires",
        corporate_tax: "IS", personal_income_tax: "IRPP",
        property_tax: "Foncier", commercial_register: "Registre du commerce",
        custom: "Personnel",
      },
      delete: "Supprimer", custom_only: "Seuls les rappels personnels peuvent être supprimés.",
    },
    chat_ext: { export_pdf: "Exporter en PDF", quota_warn: "Vous avez utilisé vos 5 consultations gratuites pour cette période. Les plans payants arrivent très bientôt — en attendant, écrivez-nous sur WhatsApp et nous ferons la mise à niveau manuellement.", upgrade_link: "Passer au plan supérieur", talk_to_lawyer: "En parler à un avocat", upgrade_whatsapp: "Nous écrire sur WhatsApp pour la mise à niveau" },
    dash_ext: { quota_remaining: "consultations restantes", unlimited: "illimité", plan: "Plan" },
    lawfirm: {
      kicker: "Quand Law Pilot ne suffit plus",
      title: "Parlez à un avocat libanais agréé",
      sub: "Pour un conseil engageant, une représentation en justice ou tout acte nécessitant une signature officielle — contactez un avocat du cabinet Eid Law Firm.",
      firm_desc: "Eid Law Firm est un cabinet libanais traitant les questions corporatives, commerciales, du travail et fiscales pour les entreprises au Liban et à l'étranger.",
      whatsapp_cta: "Discuter sur WhatsApp",
      prefill_label: "Aperçu du message",
      when_kicker: "QUAND ESCALADER",
      when_title: "Situations nécessitant un avocat",
      when_items: [
        "Un litige est porté en justice, ou vous avez reçu une assignation",
        "Vous devez signer un contrat engageant ou un acte notarié",
        "Négociation d'une sortie, transaction ou opération de M&A",
        "Rédaction d'un pacte d'actionnaires ou d'un SPA complexe",
        "Défense d'un licenciement abusif ou indemnité de fin de service",
        "Un redressement fiscal est en cours ou sous audit",
        "Toute affaire pénale, réglementaire ou de responsabilité personnelle",
      ],
      note: "En contactant Eid Law Firm, vous établissez une relation avocat-client régie par les règles du Barreau de Beyrouth. Law Pilot ne perçoit aucune commission sur cette mise en relation.",
      copy_number: "Copier le numéro",
      copied: "Numéro copié",
    },
  },
  ar: {
    nav_ext: { pricing: "الأسعار", team: "الفريق", templates: "العقود", calendar: "الروزنامة", lawfirm: "تواصل مع مكتب محاماة" },
    pricing: {
      kicker: "خطط لكل شركة لبنانية",
      title: "ادفع مقابل الوضوح، لا مقابل الساعة المُحتسبة.",
      sub: "ثلاث خطط، قابلة للإلغاء في أي وقت. الاشتراكات المدفوعة قريباً — ابدأ مجاناً اليوم.",
      mo: "/ شهرياً", cta: "اشترك", current: "الخطة الحالية", upgrade: "ترقية",
      best: "الأكثر شعبية", already: "أنت مشترك في",
      free_label: "مجاناً — 5 استشارات",
      manage: "إدارة الاشتراك",
      coming_soon: "قريباً",
    },
    billing: {
      success_title: "تم استلام الدفعة",
      success_sub: "جارٍ تفعيل خطتك…",
      verifying: "نتأكّد من حالة الدفع لدى Stripe…",
      go_dashboard: "الذهاب إلى لوحة التحكم",
      cancelled: "تم إلغاء الدفع. يمكنك المحاولة مجدّداً متى شئت.",
    },
    team: {
      title: "مساحة عمل الفريق",
      sub: "ادعُ زملاءك، أدِر المقاعد، وتعاونوا في الاستشارات.",
      members: "الأعضاء", invites: "الدعوات المعلّقة", seats: "المقاعد المستخدمة",
      invite_email: "بريد زميلك الإلكتروني", role: "الدور", send: "إرسال الدعوة",
      revoke: "إلغاء", remove: "إزالة",
      seat_limit_reached: "بلغت الحدّ الأقصى للمقاعد. ارفع خطتك لإضافة المزيد.",
      copy_link: "نسخ رابط الدعوة", copied: "تم نسخ الرابط",
      rename: "إعادة تسمية المساحة", workspace_name: "اسم المساحة",
      no_members: "لا يوجد أعضاء بعد.", no_invites: "لا توجد دعوات معلّقة.",
      join_title: "الانضمام إلى المساحة",
      join_sub: "تمّت دعوتك للانضمام إلى",
      join_btn: "قبول الدعوة",
      join_need_login: "سجّل دخولك (أو أنشئ حساباً) باستخدام البريد المدعو لقبول الدعوة.",
    },
    contracts: {
      title: "نماذج العقود",
      sub: "أنشئ عقوداً مطابقة للقانون اللبناني. راجِعها مع محامٍ قبل التوقيع.",
      pick: "اختر نموذجاً", generate: "أنشئ العقد",
      generating: "لو بايلوت يقوم بالصياغة…", preview: "معاينة",
      download_pdf: "تنزيل PDF", history: "عقودك",
      no_history: "لم تُنشئ عقوداً بعد.", delete: "حذف",
      output_lang: "لغة المخرَج",
    },
    calendar: {
      title: "الروزنامة الضريبية والعمالية",
      sub: "المواعيد القانونية في لبنان لعام 2026، مع إمكانية إضافة تذكيرات خاصة بشركتك.",
      add_event: "إضافة تذكير", event_title: "العنوان", event_date: "التاريخ",
      event_note: "ملاحظة (اختياري)", save: "حفظ",
      no_events: "لا أحداث.",
      categories: {
        nssf: "الضمان الاجتماعي", vat: "ض. القيمة المضافة", payroll_tax: "ضريبة الرواتب",
        corporate_tax: "ضريبة الشركات", personal_income_tax: "ضريبة الدخل",
        property_tax: "الأملاك المبنية", commercial_register: "السجل التجاري",
        custom: "خاص",
      },
      delete: "حذف", custom_only: "يمكن حذف التذكيرات الخاصة فقط.",
    },
    chat_ext: { export_pdf: "تصدير PDF", quota_warn: "لقد استخدمتَ استشاراتك الخمس المجانية لهذه الفترة. الخطط المدفوعة على وشك الإطلاق — إلى حينه، راسلنا على واتساب وسنقوم بترقيتك يدوياً.", upgrade_link: "ترقية الخطة", talk_to_lawyer: "تحدّث مع محامٍ حول هذا", upgrade_whatsapp: "راسلنا على واتساب للترقية" },
    dash_ext: { quota_remaining: "استشارة متبقّية", unlimited: "غير محدودة", plan: "الخطة" },
    lawfirm: {
      kicker: "عندما لا يكفي لو بايلوت",
      title: "تواصل مع محامٍ لبناني مرخّص",
      sub: "للحصول على رأي ملزم، أو تمثيل أمام المحاكم، أو لأي مسألة تتطلب توقيعاً رسمياً — تواصل مع شريك في مكتب عيد للمحاماة.",
      firm_desc: "مكتب عيد للمحاماة هو مكتب لبناني يعمل في القضايا الشركاتية والتجارية والعمالية والضريبية للشركات في لبنان وخارجه.",
      whatsapp_cta: "محادثة عبر واتساب",
      prefill_label: "معاينة الرسالة",
      when_kicker: "متى تتواصل مع محامٍ",
      when_title: "الحالات التي تستوجب محامياً مرخّصاً",
      when_items: [
        "رُفعت دعوى أمام المحاكم، أو تلقّيت تكليفاً أو إنذاراً",
        "توقيع أو تسجيل عقد ملزم أو سند رسمي لدى الكاتب العدل",
        "التفاوض على خروج، تسوية، أو صفقة استحواذ أو دمج",
        "صياغة اتفاقية مساهمين أو عقد بيع حصص معقّد",
        "الدفاع في دعوى صرف تعسفي أو تعويض نهاية خدمة",
        "تكليف ضريبي معترض عليه أو تدقيق من وزارة المالية",
        "أي ملف جزائي أو رقابي أو ينطوي على مسؤولية شخصية",
      ],
      note: "بتواصلك مع مكتب عيد للمحاماة تنشأ علاقة محامٍ-موكّل مباشرة وفقاً لقواعد نقابة المحامين في بيروت. لا تتقاضى لو بايلوت أي عمولة على هذه الإحالة.",
      copy_number: "نسخ الرقم",
      copied: "تم نسخ الرقم",
    },
  },
};

// Merge into existing dict (mutating module export is safe in CRA)
for (const lang of Object.keys(extra)) {
  Object.assign(dict[lang], extra[lang]);
}

export {};
